import * as DOMUtils from "history/DOMUtils";
import { throttle } from "lodash";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import { isRetailerApps } from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import type { NcPageUrlsModelDataType } from "../../../../common-deprecated/utils/aemPageUrlTypes";
import { RetailerCarConfigTypes, RetailerIframeMessageEventType } from "./constants";

export const sendRetailerActivity = throttle(() => {
    window.parent.window.postMessage({ type: RetailerIframeMessageEventType.IFrameActivity }, "*");
}, 3000);

export const getChromeVersion = (userAgent?: string): number | null => {
    if (DOMUtils.canUseDOM && !userAgent) userAgent = window.navigator.userAgent;
    if (!userAgent) return null;

    const chromeVersion = userAgent.match(/Chrom(e|ium)\/([0-9]+)\./)?.[2]; // will return undefined if not chrome
    return chromeVersion ? parseFloat(chromeVersion) || null : null;
};

export const getTizenVersion = (userAgent?: string): number | null => {
    if (DOMUtils.canUseDOM && !userAgent) userAgent = window.navigator.userAgent;
    if (!userAgent) return null;

    const chromeVersion = getChromeVersion(userAgent);
    let tizenVersion = userAgent.match(/Tizen ([0-9]+\.[0-9]+)/)?.[1]; // will return undefined if not Tizen OS
    if (!tizenVersion && chromeVersion && chromeVersion === 56) tizenVersion = "4.5"; // easy mock Tizen4 when using old chromium browser

    return tizenVersion ? parseFloat(tizenVersion) || null : null;
};

export const shouldUseRetailerApheleia = (
    modelCode: string,
    modelData: NcPageUrlsModelDataType[],
    commonSettings: CommonSettingsType,
): RetailerCarConfigTypes => {
    // This override link allows us to test on test devices before a model goes live
    const override = commonSettings.query.overrideCarConfigLink;
    if (override) return override;

    const { isStandalone } = commonSettings;
    const foundModel = modelData.find((model) => model.modelCode.toLowerCase() === modelCode.toLowerCase());
    if (
        (isStandalone || foundModel?.apheleiaRetailerScreensEnabledModel) &&
        foundModel?.apheleiaLiveStatusWebsite &&
        !isRetailerApps(commonSettings) // car on display, salesman & billboard will never support apheleia
    ) {
        return foundModel.useLbxJourney ? RetailerCarConfigTypes.AphLbx : RetailerCarConfigTypes.Aph;
    }
    return RetailerCarConfigTypes.Build;
};

type TruncateRetailerStringParams = {
    value: string;
    /** Maximum characters allowed per line, including the ellipsis if added */
    maxCharactersPerLine: number;
    /** Maximum number of lines to display based on maxCharactersPerLine, using spaces as word delimiters */
    maxLines: number;
};

/**
 * Truncates a string with ellipsis based on character and line limits.
 *
 * This function provides JavaScript-driven ellipsis that bypasses CSS limitations
 * for conditional truncation across multiple lines. It preserves whole words
 * when possible and handles oversized words by truncating them.
 *
 * @important When using this function, make sure to fine-tune the `maxCharactersPerLine` in a 1080p environment!
 * @important To make use of the `\n` newlines, make sure the wrapping component uses `white-space: pre-line;`
 */
export function truncateRetailerString(props: TruncateRetailerStringParams): string {
    const { value, maxCharactersPerLine, maxLines } = props;
    const ELLIPSIS = "...";
    const maxCharactersPerLineExclEllipsis = maxCharactersPerLine - ELLIPSIS.length;

    if (value.length <= maxCharactersPerLine) return value;

    const words = value.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (let index = 0; index < words.length; index++) {
        const word = words[index];

        // If we've reached max lines, truncate the last line and stop
        if (lines.length >= maxLines) {
            const lastLineIndex = lines.length - 1;
            lines[lastLineIndex] = lines[lastLineIndex].slice(0, maxCharactersPerLineExclEllipsis) + ELLIPSIS;
            break;
        }

        const spaceNeeded = currentLine ? " " : "";
        const newLine = currentLine + spaceNeeded + word;

        if (newLine.length > maxCharactersPerLine) {
            // If current line has content, push it and start a new line
            if (currentLine) {
                lines.push(currentLine);
                currentLine = "";
            }

            // Check if we can add another line
            if (lines.length >= maxLines) {
                // No more lines available, truncate last line with remaining words
                const lastLineIndex = lines.length - 1;
                lines[lastLineIndex] += ` ${word}`; // Make sure to add the current word, else this won't be part of the final result.
                lines[lastLineIndex] = lines[lastLineIndex].slice(0, maxCharactersPerLineExclEllipsis) + ELLIPSIS;
                break;
            }

            // If the new word is too long, truncate it and add it as a separate line
            if (word.length > maxCharactersPerLine) {
                const truncatedWord = word.slice(0, maxCharactersPerLineExclEllipsis) + ELLIPSIS;
                lines.push(truncatedWord);
                currentLine = "";
            } else {
                currentLine = word;
            }
        } else {
            // Word fits on current line
            currentLine = newLine;
        }

        // If this is the last word and it's still in range, add it to the result
        if (index === words.length - 1 && currentLine && lines.length < maxLines) {
            lines.push(currentLine);
        }
    }

    return lines.join("\n");
}
